/**
 * Hook + context for the resolved app mode.
 *
 * The mode is resolved once at startup (it can't change without a full navigation
 * to a different host/port), so we compute it in a provider and share via context
 * rather than recomputing in every component.
 */
import { createContext, createElement, useContext, useMemo } from 'react';

import { APP_MODES, getVariant, resolveAppMode } from './appMode';

const AppModeContext = createContext(null);

export function AppModeProvider({ children, mode }) {
  const value = useMemo(() => {
    const resolved = mode ?? resolveAppMode();
    return { mode: resolved, variant: getVariant(resolved), modes: APP_MODES };
  }, [mode]);

  return createElement(AppModeContext.Provider, { value }, children);
}

/** @returns {{mode:'cca'|'eia', variant:object, modes:typeof APP_MODES}} */
export function useAppMode() {
  const ctx = useContext(AppModeContext);
  if (!ctx) {
    throw new Error('useAppMode must be used within <AppModeProvider>.');
  }
  return ctx;
}

/** Convenience: is the current variant CCA? */
export function useIsCCA() {
  return useAppMode().mode === APP_MODES.CCA;
}

/** Convenience: is the current variant EIA? */
export function useIsEIA() {
  return useAppMode().mode === APP_MODES.EIA;
}
